import React, { useState, useEffect, useCallback } from "react";
import { API, authFetch } from "@/App";
import { toast } from "sonner";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import {
  TrendingUp, TrendingDown, Hash, Plus, Trash2, Edit2,
  RotateCcw, Loader2, Calendar, ChevronDown, X, Save, BarChart3, PieChart as PieChartIcon, LineChart as LineChartIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const COLORS = [
  "#f97316", "#3b82f6", "#10b981", "#8b5cf6", "#ef4444",
  "#f59e0b", "#06b6d4", "#ec4899", "#84cc16", "#6366f1",
  "#14b8a6", "#f43f5e", "#a855f7", "#22c55e", "#0ea5e9",
];

const CHART_TYPE_LABELS = {
  number: "Nombre",
  line: "Courbe",
  bar: "Barres",
  pie: "Camembert",
};

const OPERATION_LABELS = {
  count: "Compter",
  sum: "Somme",
  avg: "Moyenne",
  distribution: "Répartition",
  group_sum: "Somme par groupe",
  time_series: "Série temporelle",
};

// ---- Number Widget ----
function NumberWidget({ widget, data }) {
  const val = data?.value ?? 0;
  const unit = widget.unit || "";
  const isPercent = unit === "%" || widget.column?.includes("%");
  const isCurrency = widget.column?.includes("REVENUE") || widget.column?.includes("MARGIN WITHOUT");

  let display;
  if (isPercent) display = `${val.toLocaleString("fr-FR", { maximumFractionDigits: 1 })}%`;
  else if (isCurrency) display = `${val.toLocaleString("fr-FR", { maximumFractionDigits: 0 })} €`;
  else display = val.toLocaleString("fr-FR", { maximumFractionDigits: 1 });

  const compareVal = data?.compare?.value;
  let trend = null;
  if (compareVal !== undefined && compareVal !== null && compareVal !== 0) {
    const diff = ((val - compareVal) / Math.abs(compareVal)) * 100;
    trend = { diff: diff.toFixed(1), up: diff >= 0 };
  }

  return (
    <div className="flex flex-col items-center justify-center h-full py-4">
      <p className="text-3xl font-bold text-zinc-900 font-['Manrope']" data-testid="widget-number-value">{display}</p>
      {data?.count !== undefined && widget.operation !== "count" && (
        <p className="text-xs text-zinc-400 mt-1">{data.count} valeurs</p>
      )}
      {trend && (
        <div className={`flex items-center gap-1 mt-2 text-sm font-medium ${trend.up ? "text-emerald-600" : "text-red-500"}`}>
          {trend.up ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
          {trend.up ? "+" : ""}{trend.diff}%
        </div>
      )}
    </div>
  );
}

// ---- Pie Widget ----
function PieWidget({ data }) {
  const items = data?.data || [];
  if (!items.length) return <p className="text-zinc-400 text-sm text-center py-8">Aucune donnée</p>;
  return (
    <ResponsiveContainer width="100%" height={220}>
      <PieChart>
        <Pie data={items} cx="50%" cy="50%" outerRadius={75} dataKey="value" nameKey="name" label={({ name, percent }) => percent > 0.05 ? `${name} (${(percent * 100).toFixed(0)}%)` : ""} labelLine={false} style={{ fontSize: 11 }}>
          {items.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
        </Pie>
        <Tooltip formatter={(v) => v.toLocaleString("fr-FR")} />
      </PieChart>
    </ResponsiveContainer>
  );
}

// ---- Bar Widget ----
function BarWidget({ data }) {
  const items = data?.data || [];
  if (!items.length) return <p className="text-zinc-400 text-sm text-center py-8">Aucune donnée</p>;
  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={items} margin={{ top: 5, right: 10, left: 10, bottom: 40 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
        <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-35} textAnchor="end" height={60} />
        <YAxis tick={{ fontSize: 11 }} />
        <Tooltip formatter={(v) => v.toLocaleString("fr-FR")} />
        <Bar dataKey="value" fill="#f97316" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

// ---- Line Widget ----
function LineWidget({ data }) {
  const items = data?.data || [];
  if (!items.length) return <p className="text-zinc-400 text-sm text-center py-8">Aucune donnée</p>;
  return (
    <ResponsiveContainer width="100%" height={220}>
      <LineChart data={items} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
        <YAxis tick={{ fontSize: 11 }} />
        <Tooltip formatter={(v) => v.toLocaleString("fr-FR")} />
        <Line type="monotone" dataKey="value" stroke="#f97316" strokeWidth={2} dot={{ fill: "#f97316", r: 4 }} activeDot={{ r: 6 }} />
      </LineChart>
    </ResponsiveContainer>
  );
}

// ---- Widget Card Container ----
function WidgetCard({ widget, data, loading, onEdit, onDelete }) {
  const isChart = ["pie", "bar", "line"].includes(widget.chart_type);
  return (
    <div
      className={`bg-white rounded-xl border border-zinc-200 shadow-sm hover:shadow-md transition-shadow ${isChart ? "col-span-1 md:col-span-2" : "col-span-1"}`}
      data-testid={`widget-${widget.widget_id}`}
    >
      <div className="flex items-center justify-between px-4 pt-3 pb-1">
        <h3 className="text-sm font-semibold text-zinc-700 font-['Manrope'] truncate">{widget.title}</h3>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button onClick={() => onEdit(widget)} className="p-1 hover:bg-zinc-100 rounded" title="Modifier" data-testid={`widget-edit-${widget.widget_id}`}>
            <Edit2 className="w-3.5 h-3.5 text-zinc-400" />
          </button>
          <button onClick={() => onDelete(widget.widget_id)} className="p-1 hover:bg-red-50 rounded" title="Supprimer" data-testid={`widget-delete-${widget.widget_id}`}>
            <Trash2 className="w-3.5 h-3.5 text-zinc-400 hover:text-red-500" />
          </button>
        </div>
      </div>
      <div className="px-4 pb-4">
        {loading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-orange-500" /></div>
        ) : widget.chart_type === "number" ? (
          <NumberWidget widget={widget} data={data} />
        ) : widget.chart_type === "pie" ? (
          <PieWidget data={data} />
        ) : widget.chart_type === "bar" ? (
          <BarWidget data={data} />
        ) : widget.chart_type === "line" ? (
          <LineWidget data={data} />
        ) : (
          <p className="text-zinc-400 text-sm">Type inconnu: {widget.chart_type}</p>
        )}
      </div>
    </div>
  );
}

// ---- Widget Editor Modal ----
function WidgetEditorModal({ isOpen, onClose, widget, availableColumns, onSave }) {
  const [form, setForm] = useState({ title: "", column: "", operation: "count", chart_type: "number", value_column: "", unit: "" });

  useEffect(() => {
    if (widget) {
      setForm({
        title: widget.title || "",
        column: widget.column || "",
        operation: widget.operation || "count",
        chart_type: widget.chart_type || "number",
        value_column: widget.value_column || "",
        unit: widget.unit || "",
      });
    } else {
      setForm({ title: "", column: "", operation: "count", chart_type: "number", value_column: "", unit: "" });
    }
  }, [widget, isOpen]);

  const handleSave = () => {
    if (!form.title.trim() || !form.column) { toast.error("Titre et colonne requis"); return; }
    onSave({ ...form, widget_id: widget?.widget_id });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-['Manrope']">{widget?.widget_id ? "Modifier le widget" : "Nouveau widget"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Titre</Label>
            <Input value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Ex: CA Total" data-testid="widget-editor-title" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Colonne source</Label>
              <Select value={form.column} onValueChange={(v) => setForm(f => ({ ...f, column: v }))}>
                <SelectTrigger data-testid="widget-editor-column"><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                <SelectContent>{availableColumns.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Opération</Label>
              <Select value={form.operation} onValueChange={(v) => setForm(f => ({ ...f, operation: v }))}>
                <SelectTrigger data-testid="widget-editor-operation"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(OPERATION_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {form.operation === "group_sum" && (
            <div className="space-y-2">
              <Label>Colonne de valeur (pour la somme)</Label>
              <Select value={form.value_column} onValueChange={(v) => setForm(f => ({ ...f, value_column: v }))}>
                <SelectTrigger><SelectValue placeholder="Ex: REVENUE TAX INCLUDED" /></SelectTrigger>
                <SelectContent>{availableColumns.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Type d'affichage</Label>
              <Select value={form.chart_type} onValueChange={(v) => setForm(f => ({ ...f, chart_type: v }))}>
                <SelectTrigger data-testid="widget-editor-chart-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(CHART_TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Unité (optionnel)</Label>
              <Input value={form.unit} onChange={(e) => setForm(f => ({ ...f, unit: e.target.value }))} placeholder="€, %, kg..." />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={handleSave} className="bg-orange-500 hover:bg-orange-600 text-white gap-2" data-testid="widget-editor-save">
            <Save className="w-4 h-4" />{widget?.widget_id ? "Mettre à jour" : "Créer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---- Period Selector ----
function PeriodSelector({ period, setPeriod, dateFrom, setDateFrom, dateTo, setDateTo, compareFrom, setCompareFrom, compareTo, setCompareTo }) {
  const [showCompare, setShowCompare] = useState(false);

  const presets = [
    { label: "Tout", value: "all" },
    { label: "Ce mois", value: "this_month" },
    { label: "Mois dernier", value: "last_month" },
    { label: "Cette semaine", value: "this_week" },
    { label: "Sem. dernière", value: "last_week" },
    { label: "Personnalisé", value: "custom" },
  ];

  const applyPreset = (preset) => {
    setPeriod(preset);
    const now = new Date();
    if (preset === "all") { setDateFrom(""); setDateTo(""); }
    else if (preset === "this_month") {
      setDateFrom(new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split("T")[0]);
      setDateTo(new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split("T")[0]);
    } else if (preset === "last_month") {
      setDateFrom(new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString().split("T")[0]);
      setDateTo(new Date(now.getFullYear(), now.getMonth(), 0).toISOString().split("T")[0]);
    } else if (preset === "this_week") {
      const day = now.getDay() || 7;
      const monday = new Date(now); monday.setDate(now.getDate() - day + 1);
      setDateFrom(monday.toISOString().split("T")[0]);
      setDateTo(now.toISOString().split("T")[0]);
    } else if (preset === "last_week") {
      const day = now.getDay() || 7;
      const lastMonday = new Date(now); lastMonday.setDate(now.getDate() - day - 6);
      const lastSunday = new Date(lastMonday); lastSunday.setDate(lastMonday.getDate() + 6);
      setDateFrom(lastMonday.toISOString().split("T")[0]);
      setDateTo(lastSunday.toISOString().split("T")[0]);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-3" data-testid="period-selector">
      <div className="flex items-center gap-1 bg-white border border-zinc-200 rounded-lg p-1">
        {presets.map(p => (
          <button key={p.value} onClick={() => applyPreset(p.value)}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${period === p.value ? "bg-orange-500 text-white" : "text-zinc-600 hover:bg-zinc-100"}`}
            data-testid={`period-${p.value}`}
          >{p.label}</button>
        ))}
      </div>
      {period === "custom" && (
        <div className="flex items-center gap-2">
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-8 px-2 text-xs border border-zinc-200 rounded-md" data-testid="date-from" />
          <span className="text-zinc-400 text-xs">→</span>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-8 px-2 text-xs border border-zinc-200 rounded-md" data-testid="date-to" />
        </div>
      )}
      <button onClick={() => setShowCompare(!showCompare)}
        className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${showCompare ? "bg-blue-50 border-blue-300 text-blue-700" : "border-zinc-200 text-zinc-600 hover:bg-zinc-50"}`}
        data-testid="toggle-compare"
      >
        <BarChart3 className="w-3.5 h-3.5" />Comparer
      </button>
      {showCompare && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-zinc-500">vs</span>
          <input type="date" value={compareFrom} onChange={(e) => setCompareFrom(e.target.value)} className="h-8 px-2 text-xs border border-zinc-200 rounded-md" data-testid="compare-from" />
          <span className="text-zinc-400 text-xs">→</span>
          <input type="date" value={compareTo} onChange={(e) => setCompareTo(e.target.value)} className="h-8 px-2 text-xs border border-zinc-200 rounded-md" data-testid="compare-to" />
        </div>
      )}
    </div>
  );
}

// ---- Main Analytics Component ----
export function AdminAnalytics() {
  const [widgets, setWidgets] = useState([]);
  const [widgetData, setWidgetData] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingWidgets, setLoadingWidgets] = useState({});
  const [availableColumns, setAvailableColumns] = useState([]);

  // Period state
  const [period, setPeriod] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [compareFrom, setCompareFrom] = useState("");
  const [compareTo, setCompareTo] = useState("");

  // Editor modal
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingWidget, setEditingWidget] = useState(null);

  // Load widgets and columns
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [widgetsRes, colsRes] = await Promise.all([
          authFetch(`${API}/analytics/widgets`),
          authFetch(`${API}/analytics/columns`),
        ]);
        if (widgetsRes.ok) {
          const d = await widgetsRes.json();
          setWidgets(d.widgets || []);
        }
        if (colsRes.ok) {
          const d = await colsRes.json();
          setAvailableColumns(d.columns || []);
        }
      } catch (e) {
        console.error("Failed to load analytics:", e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Fetch data for all widgets when widgets or period changes
  const fetchAllWidgetData = useCallback(async () => {
    if (!widgets.length) return;
    const newLoadingState = {};
    widgets.forEach(w => { newLoadingState[w.widget_id] = true; });
    setLoadingWidgets(newLoadingState);

    const params = new URLSearchParams();
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    if (compareFrom) params.set("compare_from", compareFrom);
    if (compareTo) params.set("compare_to", compareTo);

    const results = {};
    await Promise.all(widgets.map(async (w) => {
      try {
        const qp = new URLSearchParams(params);
        qp.set("column", w.column);
        qp.set("operation", w.operation);
        if (w.value_column) qp.set("value_column", w.value_column);
        const res = await authFetch(`${API}/analytics/aggregate?${qp.toString()}`);
        if (res.ok) results[w.widget_id] = await res.json();
      } catch (e) {
        console.error(`Widget ${w.widget_id} fetch failed:`, e);
      } finally {
        setLoadingWidgets(prev => ({ ...prev, [w.widget_id]: false }));
      }
    }));
    setWidgetData(results);
  }, [widgets, dateFrom, dateTo, compareFrom, compareTo]);

  useEffect(() => { fetchAllWidgetData(); }, [fetchAllWidgetData]);

  const handleDeleteWidget = async (widgetId) => {
    try {
      const res = await authFetch(`${API}/analytics/widgets/${widgetId}`, { method: "DELETE" });
      if (res.ok) {
        setWidgets(prev => prev.filter(w => w.widget_id !== widgetId));
        toast.success("Widget supprimé");
      }
    } catch (e) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleSaveWidget = async (form) => {
    try {
      if (form.widget_id) {
        // Update
        const res = await authFetch(`${API}/analytics/widgets/${form.widget_id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(form),
        });
        if (res.ok) {
          setWidgets(prev => prev.map(w => w.widget_id === form.widget_id ? { ...w, ...form } : w));
          toast.success("Widget mis à jour");
        }
      } else {
        // Create
        const res = await authFetch(`${API}/analytics/widgets`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(form),
        });
        if (res.ok) {
          const d = await res.json();
          setWidgets(prev => [...prev, { ...form, widget_id: d.widget_id }]);
          toast.success("Widget créé");
        }
      }
      setEditorOpen(false);
      setEditingWidget(null);
      // Refresh data
      setTimeout(fetchAllWidgetData, 300);
    } catch (e) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const handleResetDefaults = async () => {
    try {
      const res = await authFetch(`${API}/analytics/widgets/reset-defaults`, { method: "POST" });
      if (res.ok) {
        const wRes = await authFetch(`${API}/analytics/widgets`);
        if (wRes.ok) {
          const d = await wRes.json();
          setWidgets(d.widgets || []);
          toast.success("Widgets réinitialisés");
          setTimeout(fetchAllWidgetData, 300);
        }
      }
    } catch (e) {
      toast.error("Erreur lors de la réinitialisation");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-analytics">
      {/* Header controls */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <PeriodSelector
          period={period} setPeriod={setPeriod}
          dateFrom={dateFrom} setDateFrom={setDateFrom}
          dateTo={dateTo} setDateTo={setDateTo}
          compareFrom={compareFrom} setCompareFrom={setCompareFrom}
          compareTo={compareTo} setCompareTo={setCompareTo}
        />
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleResetDefaults} className="gap-1.5 text-xs" data-testid="reset-widgets-btn">
            <RotateCcw className="w-3.5 h-3.5" />Réinitialiser
          </Button>
          <Button size="sm" onClick={() => { setEditingWidget(null); setEditorOpen(true); }} className="bg-orange-500 hover:bg-orange-600 text-white gap-1.5 text-xs" data-testid="add-widget-btn">
            <Plus className="w-3.5 h-3.5" />Nouveau widget
          </Button>
        </div>
      </div>

      {/* Widget Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 group" data-testid="widget-grid">
        {widgets.map(w => (
          <WidgetCard
            key={w.widget_id}
            widget={w}
            data={widgetData[w.widget_id]}
            loading={loadingWidgets[w.widget_id]}
            onEdit={(ww) => { setEditingWidget(ww); setEditorOpen(true); }}
            onDelete={handleDeleteWidget}
          />
        ))}
      </div>

      {!widgets.length && (
        <div className="text-center py-16 text-zinc-400">
          <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm">Aucun widget configuré</p>
          <Button size="sm" variant="outline" className="mt-4 gap-1.5" onClick={() => { setEditingWidget(null); setEditorOpen(true); }}>
            <Plus className="w-4 h-4" />Ajouter un widget
          </Button>
        </div>
      )}

      {/* Widget Editor */}
      <WidgetEditorModal
        isOpen={editorOpen}
        onClose={() => { setEditorOpen(false); setEditingWidget(null); }}
        widget={editingWidget}
        availableColumns={availableColumns}
        onSave={handleSaveWidget}
      />
    </div>
  );
}
